import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// ==================== DATA ====================

// Newest first. Each image `alt` carries what the flyer says, in its own language.
// `eyebrow` and an image `caption` are optional.
const curriculumItems = [
  {
    id: 'win-period',
    title: 'What is WIN?',
    body: 'PS 85 has implemented a WIN period for Kindergarten through 5th grade. Please see the attached flyers to learn about the WIN period.',
    images: [
      {
        src: '/assets/images/WIN_EN.webp',
        width: 1320,
        height: 1981,
        caption: 'English',
        alt: 'PS 85 What is WIN? Extra Support. Bigger Growth. Small groups every day for 30 minutes. What is a WIN period? A WIN period is a dedicated block of time in our schedule to give students extra academic support. What is the purpose? To strengthen their decoding and encoding abilities. How will you know it’s working? We will be sending home data reports that show your child’s progress. What will it look like? Your child will work in small groups using Sound Sensible, Spire and Phonics for Reading, structured literacy and reading intervention programs. Together, we can help every child build strong reading and writing skills!',
      },
      {
        src: '/assets/images/WIN_ES.webp',
        width: 1320,
        height: 1976,
        caption: 'Español',
        alt: 'PS 85 ¿Qué es WIN? Apoyo adicional. Mayor crecimiento. Grupos pequeños cada día durante 30 minutos. ¿Qué es un período WIN? Un período WIN es un bloque de tiempo dedicado en nuestro horario para brindar apoyo académico adicional a los estudiantes. ¿Cuál es el propósito? Fortalecer sus habilidades de decodificación y codificación. ¿Cómo sabré que está funcionando? Enviaremos informes de datos de progreso que mostrarán el avance de su hijo/a. ¿Cómo se verá? Su hijo/a trabajará en grupos pequeños utilizando Sound Sensible, Spire y Fonética para la lectura, programas de alfabetización estructurada y de intervención en la lectura. ¡Juntos, podemos ayudar a cada niño/a a construir sólidas habilidades de lectura y escritura!',
        lang: 'es',
      },
    ],
  },
];

// ==================== REUSABLE COMPONENTS ====================

const Container = ({ className = "", children }) => (
  <div className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

const CurriculumCard = ({ eyebrow, title, body, images }) => (
  <article className="p-6 md:p-10 bg-white rounded-3xl border border-gray-100 shadow-soft">
    <div className="max-w-3xl">
      {eyebrow && (
        <span className="inline-flex items-center gap-2 mb-3 text-school-goldDark font-semibold tracking-[0.18em] uppercase text-xs">
          <span className="h-1.5 w-1.5 rounded-full bg-school-gold" aria-hidden="true" />
          {eyebrow}
        </span>
      )}
      <h2 className="text-3xl sm:text-4xl font-heading font-bold text-school-blue leading-[1.08] tracking-tight">
        {title}
      </h2>
      <p className="mt-4 text-base md:text-lg text-gray-600 leading-7">{body}</p>
    </div>

    <div className="mt-8 grid md:grid-cols-2 gap-6">
      {images.map(({ src, width, height, caption, alt, lang }) => (
        <figure key={src}>
          <img
            src={src}
            width={width}
            height={height}
            alt={alt}
            lang={lang}
            loading="lazy"
            className="w-full h-auto rounded-2xl shadow-sm"
          />
          {caption && (
            <figcaption className="mt-2 text-sm font-semibold text-gray-600" lang={lang}>{caption}</figcaption>
          )}
        </figure>
      ))}
    </div>
  </article>
);

// ==================== MAIN COMPONENT ====================

const Curriculum = () => (
  <div className="min-h-screen font-sans text-gray-600 antialiased selection:bg-school-gold/30 selection:text-school-blue" style={{ backgroundColor: '#F5EFE0' }}>
    <Navbar />

    <main id="main-content">

      {/* ==================== HERO SECTION ==================== */}
      <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center overflow-hidden bg-[#0F2240]">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.35),transparent_55%)]" aria-hidden="true" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 100%, rgba(197,145,50,0.1) 0%, transparent 70%)' }} />

        <Container className="relative z-10 pt-32 pb-16 md:pt-40 md:pb-20">
          <div className="max-w-4xl bg-[#0F2240]">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-xs sm:text-sm font-semibold mb-7">
              <span className="w-2 h-2 rounded-full bg-school-gold" aria-hidden="true" />
              Parent Portal
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-heading font-bold text-white tracking-tight leading-[1.05]">
              Curriculum
            </h1>
          </div>
        </Container>
      </section>

      {/* ==================== CURRICULUM ==================== */}
      <section className="py-20 md:py-28 bg-[#F5EFE0]">
        <Container className="space-y-10">
          {curriculumItems.map(({ id, ...item }) => (
            <CurriculumCard key={id} {...item} />
          ))}
        </Container>
      </section>

    </main>

    <Footer />
  </div>
);

export default Curriculum;
